import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { SITE } from '../mock';

// Google Apps Script Web App endpoint — leads are appended to your Google Sheet
const LEADS_WEBHOOK_URL =
  'https://script.google.com/macros/s/AKfycbxD2QO14zDow4pOGPfqSOhLEyqVcgQykjBPXrJuWENiG4ggID5hTnbJ4k3xy9BpsuI6Xw/exec';

export default function OptinPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your full name';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Please enter a valid email';
    if (!/^[0-9+\-\s()]{7,}$/.test(form.phone)) e.phone = 'Please enter a valid phone number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);

    // Save locally as a safety backup
    try {
      const existing = JSON.parse(localStorage.getItem('nt_optin_leads') || '[]');
      existing.push({ ...form, ts: Date.now() });
      localStorage.setItem('nt_optin_leads', JSON.stringify(existing));
    } catch {
      /* ignore */
    }

    // Send to Google Sheets via Apps Script.
    // Use text/plain to avoid CORS preflight (Apps Script still parses JSON body).
    try {
      await fetch(LEADS_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          source: 'optin-page',
          page: window.location.pathname,
          referrer: document.referrer || '',
          userAgent: navigator.userAgent || '',
        }),
      });
    } catch {
      /* network failure — lead still saved locally, continue to VSL */
    }

    setSubmitted(true);
    // Redirect to training page after a brief confirmation flash
    setTimeout(() => navigate('/training'), 900);
  };

  return (
    <div className="min-h-screen bg-navy-950 text-white">
      {/* Subtle ambient glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full bg-amber-300/[0.06] blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-[500px] h-[500px] rounded-full bg-amber-500/[0.04] blur-3xl" />
      </div>

      {/* Back link removed for Meta ads landing experience */}

      <main className="relative max-w-4xl mx-auto px-6 pt-16 pb-24">
        {/* Brand monogram */}
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <div className="absolute -inset-6 bg-amber-300/10 blur-2xl rounded-full" />
            <div className="relative w-20 h-20 rounded-2xl border border-amber-300/30 bg-gradient-to-b from-navy-900 to-navy-950 flex items-center justify-center">
              <span className="font-serif text-3xl bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent">
                NT
              </span>
            </div>
          </div>
          <p className="mt-4 text-[11px] tracking-[0.28em] uppercase bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent font-semibold">
            Nishit Thakkar &middot; 1-to-1 Consulting
          </p>
        </div>

        {/* Eligibility ribbon */}
        <div className="mt-10 mx-auto max-w-2xl rounded-full bg-gradient-to-r from-amber-200 via-amber-300 to-amber-200 text-navy-900 px-5 py-2.5 text-center text-[13px] md:text-[14px] font-semibold tracking-wide">
          If You're Not Serious About Rewiring Your Money Mind, This Isn't for You
        </div>

        {/* Headline */}
        <h1 className="mt-10 font-serif text-center text-white text-[34px] md:text-[54px] leading-[1.05] tracking-tight">
          <span className="block bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">
            We Hand-Hold Ambitious Souls
          </span>
          <span className="block bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">
            To Command Wealth With{' '}
            <span className="inline-block bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent italic">
              Proven Systems.
            </span>
          </span>
        </h1>

        {/* USP badge */}
        <p className="mt-6 text-center font-serif text-[20px] md:text-[26px] bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent">
          Manifestation Meets Business Implementation&trade;
        </p>

        {/* Card / Form */}
        <div className="mt-10 mx-auto max-w-xl">
          <div className="relative rounded-2xl border border-white/10 bg-navy-900/60 backdrop-blur p-6 md:p-8">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-amber-300/10 to-transparent pointer-events-none" />

            {submitted ? (
              <SuccessState onReset={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '' }); }} />
            ) : (
              <form onSubmit={handleSubmit} className="relative space-y-4">
                <Field
                  label="Full Name"
                  value={form.name}
                  onChange={(v) => setForm((s) => ({ ...s, name: v }))}
                  placeholder="Your full name"
                  error={errors.name}
                  autoComplete="name"
                />
                <Field
                  label="Email Address"
                  value={form.email}
                  onChange={(v) => setForm((s) => ({ ...s, email: v }))}
                  placeholder="you@example.com"
                  type="email"
                  error={errors.email}
                  autoComplete="email"
                />
                <Field
                  label="Phone (WhatsApp preferred)"
                  value={form.phone}
                  onChange={(v) => setForm((s) => ({ ...s, phone: v }))}
                  placeholder="+91 98xxxxxxxx"
                  type="tel"
                  error={errors.phone}
                  autoComplete="tel"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="group w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-[15px] font-semibold text-navy-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_10px_36px_-6px_rgba(251,191,36,0.55)] hover:-translate-y-[1px] hover:shadow-[0_16px_44px_-6px_rgba(251,191,36,0.75)] transition-all disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <Sparkles size={16} className={submitting ? 'animate-pulse' : ''} />
                  {submitting ? 'Submitting…' : 'Apply For 1-to-1 Consulting'}
                </button>

                <p className="text-center text-[11px] tracking-[0.18em] uppercase text-neutral-500 pt-2">
                  Limited slots &middot; By application only
                </p>
              </form>
            )}
          </div>

          {/* Trust strip — note: no money-back guarantee */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[
              { label: '1-to-1\nPrivate Sessions' },
              { label: 'Inner Work +\nBusiness Systems' },
              { label: 'Application\nReviewed in 48h' },
            ].map((t) => (
              <div
                key={t.label}
                className="rounded-xl border border-white/10 bg-navy-900/40 px-3 py-3 text-[11px] tracking-[0.14em] uppercase text-neutral-300 whitespace-pre-line"
              >
                {t.label}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-20 border-t border-white/5 pt-6 text-center">
          <div className="flex items-center justify-center gap-5 text-[12px] text-neutral-500">
            <span>Privacy Policy</span>
            <span className="text-neutral-700">|</span>
            <span>Terms &amp; Conditions</span>
          </div>
          <p className="mt-4 max-w-2xl mx-auto text-[11px] text-neutral-600 leading-relaxed">
            DISCLAIMER: NishitThakkar.in does not guarantee any specific results or outcomes. Any
            client examples shown are for demonstration / educational purposes only and must not be
            interpreted as promised or expected outcomes. Your results depend on your effort,
            mindset work, and consistent implementation.
          </p>
          <p className="mt-4 text-[12px] text-neutral-500">
            &copy; {new Date().getFullYear()} {SITE.brandTop} {SITE.brandBottom}. All Rights Reserved.
          </p>
        </footer>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', error, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.2em] uppercase text-neutral-400 mb-2">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`w-full rounded-xl bg-navy-950/70 border ${
          error ? 'border-red-400/60' : 'border-white/10'
        } px-4 py-3.5 text-[15px] text-white placeholder:text-neutral-600 outline-none focus:border-amber-300/60 focus:ring-2 focus:ring-amber-300/20 transition-colors`}
      />
      {error && (
        <span className="mt-1.5 block text-xs text-red-300">{error}</span>
      )}
    </label>
  );
}

function SuccessState({ onReset }) {
  return (
    <div className="relative text-center py-6">
      <div className="mx-auto w-14 h-14 rounded-full bg-amber-300/15 border border-amber-300/30 flex items-center justify-center text-amber-300">
        <CheckCircle2 size={28} strokeWidth={1.6} />
      </div>
      <h3 className="mt-5 font-serif text-white text-[26px] md:text-[30px] leading-tight">
        Application Received.
      </h3>
      <p className="mt-3 text-neutral-400 text-[14px] md:text-[15px] leading-relaxed max-w-md mx-auto">
        Redirecting you to the training video now&hellip;
      </p>
    </div>
  );
}
